import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, TrendingUp, Trophy, Activity } from 'lucide-react';
import { wrappedTranslations } from '../../../translations/wrapped';

interface StatsOverviewCardProps {
    totalGames: number;
    winPercentage: number;
    eloChange: number;
    peakElo: number;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const StatsOverviewCard: React.FC<StatsOverviewCardProps> = ({
    totalGames,
    winPercentage,
    eloChange,
    peakElo,
    lang,
}) => {
    const t = wrappedTranslations[lang];

    // Safely handle all numeric values
    const safeWinPercentage = isNaN(winPercentage) ? 0 : Math.min(100, winPercentage);
    const safeEloChange = isNaN(eloChange) ? 0 : eloChange;

    const stats = [
        {
            label: t.matchesPlayed,
            value: totalGames || 0,
            icon: Calendar,
            delay: 0.6
        },
        {
            label: t.winRate,
            value: `${safeWinPercentage.toFixed(0)}%`,
            icon: Trophy,
            delay: 0.7
        },
        {
            label: t.eloChange,
            value: safeEloChange > 0 ? `+${safeEloChange}` : safeEloChange,
            icon: TrendingUp,
            delay: 0.8
        },
        {
            label: t.peakElo,
            value: peakElo || 0,
            icon: Activity,
            delay: 0.9
        }
    ];

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-6xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 20 }}
                >
                    <Calendar className="w-20 h-20 md:w-24 md:h-24 mb-8 mx-auto text-[#1C7AD6] drop-shadow-[0_0_25px_rgba(28,122,214,0.4)]" />
                </motion.div>

                <motion.h2
                    className="text-2xl md:text-3xl font-bold mb-6 md:mb-12 text-gray-900 tracking-tight"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.yourYearInNumbers}
                </motion.h2>

                <div className="grid grid-cols-2 gap-3 md:gap-8 w-full px-2 md:px-0">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            className="rounded-2xl p-3 md:p-8 border border-gray-200 bg-white flex flex-col items-center justify-center aspect-square md:aspect-auto md:h-72 overflow-hidden shadow-lg"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: stat.delay }}
                        >
                            <stat.icon className="w-6 h-6 md:w-10 md:h-10 mb-2 md:mb-6 text-[#1C7AD6] shrink-0" />
                            <div className="text-3xl sm:text-4xl md:text-6xl font-bold mb-1 md:mb-3 text-[#1C7AD6] tracking-tighter break-words max-w-full px-1">
                                {stat.value}
                            </div>
                            <div className="text-[10px] md:text-xs text-gray-600 uppercase tracking-wider text-center leading-tight px-1">
                                {stat.label}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default StatsOverviewCard;
